// Define the shape of our API responses
interface GoogleLocation {
  lat: number;
  lng: number;
}

interface GooglePlace {
  id: string;
  place_id?: string; // Sometimes present in autocomplete
  name: string;
  address: string;
  main_text?: string;
  secondary_text?: string;
  google_maps_uri?: string;
  location?: GoogleLocation;
}

// Augment the global Window interface
declare global {
  interface Window {
    lastKnownLocation?: GoogleLocation;
  }
}

function initVenueSelector(): void {
  const root = document.getElementById("venue-selector-root");
  if (!root) return;

  // Cast DOM elements to their specific types
  const searchInterface = document.getElementById(
    "search-interface",
  ) as HTMLDivElement;
  const selectedDisplay = document.getElementById(
    "selected-venue-display",
  ) as HTMLDivElement;
  const input = document.getElementById(
    "venue-autocomplete",
  ) as HTMLInputElement;
  const resultsContainer = document.getElementById(
    "venue-results",
  ) as HTMLDivElement;
  const locateBtn = document.getElementById(
    "locate-me-btn",
  ) as HTMLButtonElement;
  const errorContainer = document.getElementById(
    "venue-search-error",
  ) as HTMLDivElement;

  // Hidden inputs for form submission
  const finalNameInput = document.getElementById(
    "final_venue_name",
  ) as HTMLInputElement;
  const finalLinkInput = document.getElementById(
    "final_maps_link",
  ) as HTMLInputElement;
  const finalExternalIdInput = document.getElementById(
    "final_external_id",
  ) as HTMLInputElement;
  const finalLatitudeInput = document.getElementById(
    "final_latitude",
  ) as HTMLInputElement;
  const finalLongitudeInput = document.getElementById(
    "final_longitude",
  ) as HTMLInputElement;
  const finalAddressInput = document.getElementById(
    "final_address",
  ) as HTMLInputElement;

  // Check if critical elements exist before proceeding
  if (!input || !resultsContainer || !finalNameInput) return;

  let sessionToken: string = crypto.randomUUID();
  let debounceTimer: number | undefined;

  function showError(msg: string): void {
    if (!errorContainer) return;
    errorContainer.textContent = msg;
    errorContainer.style.display = "block";
    setTimeout(() => {
      errorContainer.style.display = "none";
    }, 5000);
  }

  // UI State Management
  function showSelection(
    name: string,
    address: string | null,
    link: string | null,
    id: string | null,
    lat: number | string | null,
    lng: number | string | null,
  ): void {
    const nameDisplay = document.getElementById("selected-venue-name");
    const addressDisplay = document.getElementById("selected-venue-address");

    if (nameDisplay) nameDisplay.textContent = name;
    if (addressDisplay) addressDisplay.textContent = address || "";

    finalNameInput.value = name;
    finalLinkInput.value = link || "";
    finalExternalIdInput.value = id || "";
    finalLatitudeInput.value = lat?.toString() || "";
    finalLongitudeInput.value = lng?.toString() || "";
    finalAddressInput.value = address || "";

    if (searchInterface) searchInterface.style.display = "none";
    if (selectedDisplay) selectedDisplay.style.display = "flex";
    if (errorContainer) errorContainer.style.display = "none";
  }

  function clearSelection(): void {
    finalNameInput.value = "";
    finalLinkInput.value = "";
    finalExternalIdInput.value = "";
    finalLatitudeInput.value = "";
    finalLongitudeInput.value = "";
    finalAddressInput.value = "";
    input.value = "";

    if (selectedDisplay) selectedDisplay.style.display = "none";
    if (searchInterface) searchInterface.style.display = "block";
    sessionToken = crypto.randomUUID(); // Reset session
  }

  function renderResults(
    items: GooglePlace[],
    isAutocomplete: boolean = true,
  ): void {
    resultsContainer.innerHTML = "";
    if (items.length === 0) {
      resultsContainer.style.display = "none";
      return;
    }

    items.forEach((item) => {
      const div = document.createElement("div");
      div.style.padding = "8px 12px";
      div.style.cursor = "pointer";
      div.style.borderBottom = "1px solid #f0f0f0";
      div.onmouseover = () => (div.style.background = "#f9f9f9");
      div.onmouseout = () => (div.style.background = "transparent");

      if (isAutocomplete) {
        // Autocomplete results typically have main_text/secondary_text
        div.innerHTML = `<strong>${item.main_text}</strong><br><small style="color:#666">${item.secondary_text || ""}</small>`;
        // Use place_id for details fetch
        if (item.place_id) {
          div.onclick = () => fetchDetails(item.place_id!);
        }
      } else {
        // Nearby results are full place objects
        div.innerHTML = `<strong>${item.name}</strong><br><small style="color:#666">${item.address || ""}</small>`;
        div.onclick = () =>
          showSelection(
            item.name,
            item.address,
            item.location
              ? `https://www.google.com/maps/search/?api=1&query=${item.location.lat},${item.location.lng}&query_place_id=${item.id}`
              : "",
            item.id,
            item.location ? item.location.lat : "",
            item.location ? item.location.lng : "",
          );
      }
      resultsContainer.appendChild(div);
    });
    resultsContainer.style.display = "block";
  }

  // API Interactions
  async function fetchAutocomplete(query: string): Promise<void> {
    if (query.length < 3) return;

    // Get current location for bias if available
    let lat: number | string = "";
    let lng: number | string = "";

    if (window.lastKnownLocation) {
      lat = window.lastKnownLocation.lat;
      lng = window.lastKnownLocation.lng;
    }

    try {
      const res = await fetch(
        `/api/places/autocomplete?q=${encodeURIComponent(query)}&session_token=${sessionToken}&lat=${lat}&lng=${lng}`,
      );
      if (res.ok) {
        const data: GooglePlace[] = await res.json();
        renderResults(data, true);
      }
    } catch (e) {
      console.error("Autocomplete error", e);
    }
  }

  async function fetchDetails(placeId: string): Promise<void> {
    try {
      const res = await fetch(
        `/api/places/${placeId}?session_token=${sessionToken}`,
      );
      if (res.ok) {
        const place: GooglePlace = await res.json();
        showSelection(
          place.name,
          place.address,
          place.google_maps_uri || null,
          place.id,
          place.location ? place.location.lat : "",
          place.location ? place.location.lng : "",
        );
      } else {
        showError("Could not fetch place details.");
      }
    } catch (e) {
      console.error("Details error", e);
      showError("Connection error while fetching details.");
    }
  }

  async function fetchNearby(lat: number, lng: number): Promise<void> {
    if (locateBtn) locateBtn.textContent = "Loading...";
    try {
      const res = await fetch(
        `/api/places/nearby_restaurants?lat=${lat}&lng=${lng}&radius_m=1000`,
      );
      if (res.ok) {
        const data: GooglePlace[] = await res.json();
        if (data.length === 0) {
          showError("No restaurants found nearby.");
        }
        renderResults(data, false);
      } else {
        showError("Failed to fetch nearby places.");
      }
    } catch (e) {
      console.error("Nearby error", e);
      showError("Connection error while fetching nearby places.");
    } finally {
      if (locateBtn) locateBtn.textContent = "📍 Near me";
    }
  }

  // Event Listeners
  input.addEventListener("focus", () => {
    if (!window.lastKnownLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          window.lastKnownLocation = { lat: latitude, lng: longitude };
          // Optionally re-trigger search if input has value
          if (input.value.length >= 3) {
            fetchAutocomplete(input.value);
          }
        },
        (err) => {
          console.log(
            "Location access denied or unavailable for autocomplete bias",
            err,
          );
        },
        { timeout: 5000, maximumAge: 60000 },
      );
    }
  });

  input.addEventListener("input", (e) => {
    const target = e.target as HTMLInputElement;
    clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      fetchAutocomplete(target.value);
    }, 300);
  });

  if (locateBtn) {
    locateBtn.addEventListener("click", () => {
      if (!navigator.geolocation) {
        showError("Geolocation is not supported by your browser");
        return;
      }
      locateBtn.textContent = "Locating...";
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          window.lastKnownLocation = { lat: latitude, lng: longitude };
          fetchNearby(latitude, longitude);
        },
        (err) => {
          console.warn("Geolocation denied/error", err);
          showError(
            "Unable to retrieve your location. Please check permissions.",
          );
          locateBtn.textContent = "📍 Near me";
        },
      );
    });
  }

  const clearBtn = document.getElementById("clear-venue-btn");
  if (clearBtn) {
    clearBtn.addEventListener("click", clearSelection);
  }
}

// Support both Turbo and standard DOMContentLoaded
document.addEventListener("turbo:load", initVenueSelector);
document.addEventListener("DOMContentLoaded", initVenueSelector);
